import { reactive, ref } from 'vue';
import { debounce } from 'lodash-es';
import BaseSetupService from '@/setup/BaseSetupService';
import GoogleMapApi from '@/api/GoogleMapApi';

type Props = SetupProps<typeof SharedGoogleMapSetupService.props>

export default class SharedGoogleMapSetupService extends BaseSetupService<Props> {
	static readonly props = {
		isCover: Boolean,
		latitude: {
			type: Number,
			required: true as const,
		},
		longitude: {
			type: Number,
			required: true as const,
		},
	};

	readonly _map = ref<HTMLElement>();

	private readonly debouncedStaticMap = debounce((ctx) => {
		ctx.refreshStaticMapAsync();
	}, 1000);

	private readonly state = reactive({
		backgroundUrl: '',
		resizeObserver: undefined as Undef<ResizeObserver>,
	});

	get backgroundUrl () { return this.state.backgroundUrl; }


	constructor (props?: Props) {
		super(props);
	}


	protected onMounted () {
		if (typeof window.ResizeObserver !== 'undefined' && this._map.value) {
			this.state.resizeObserver = new ResizeObserver(() => this.triggerStaticMap());
			this.state.resizeObserver.observe(this._map.value);
		}
	}

	protected onBeforeUnmount () {
		if (this._map.value) {
			this.state.resizeObserver?.unobserve(this._map.value);
		}
	}


	triggerStaticMap () {
		if (!this.state.backgroundUrl) {
			this.refreshStaticMapAsync();
			return;
		}
		this.debouncedStaticMap(this);
	}

	async refreshStaticMapAsync () {
		if (!this.props.latitude || !this.props.longitude) return;
		if (!this._map.value) return;
		if (!this._map.value.clientHeight || !this._map.value.clientWidth) return;

		let dimensions = [this._map.value.clientWidth, this._map.value.clientHeight];

		if (this.props.isCover) {
			const biggestSideIndex = dimensions[0] > dimensions[1] ? 0 : 1;

			if (dimensions[biggestSideIndex] > 640) {

				const ratio = 640 / dimensions[biggestSideIndex];
				dimensions = dimensions.map(x => Math.round(x * ratio));
			}
		}

		const { data } = await GoogleMapApi.get({
			latitude: this.props.latitude,
			longitude: this.props.longitude,
			scale: 2,
			zoom: 15,
			width: dimensions[0],
			height: dimensions[1],
			showMarker: true,
		});

		this.state.backgroundUrl = data;
	}

	redirectToGoogleMap () {
		const url = new URL(`https://www.google.com/maps/search/?api=1&query=${this.props.latitude},${this.props.longitude}`);

		url.searchParams.append('query', `${this.props.latitude},${this.props.longitude}`);

		window.open(url.href, 'about:blank');
	}
}
